import { useId } from 'react';
import { Badge, EmptyState, Skeleton } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { cx } from '@/lib/cx';
import type { LinkCandidate } from '@/lib/types';

/**
 * PICKS THE ONE DOCUMENT LINE A MANUALLY ADDED ROW "WILL BE BILLED ON"
 * (owner, 2026-10-16).
 *
 * A presentational single-select over `LinkCandidate[]`, shared by the row's
 * "Link" modal (candidates from `GET /client-deliverables/:id/link-candidates`)
 * and by `DeliverableFormModal` (candidates adapted client-side in
 * `lib/deliverable-link.ts`, because the row does not exist yet). It fetches
 * nothing and decides nothing: the prop contract is the whole interface, and
 * the "Link" modal imports it — keep it exact.
 *
 * - `candidates === null` is NOT LOADED, never "none": it renders the skeleton,
 *   exactly as `loading` does. `[]` is the empty state.
 * - Grouped by kind in a fixed order (Deal, Quote, Invoice); within a kind the
 *   caller's order is kept.
 * - One radio group across all three kinds, so exactly one line is chosen. The
 *   identity is `{kind, lineId}` — a deal line and a quote line can share a
 *   numeric id.
 * - Quantity is a decimal string rendered as given (house money/number rule).
 */

type LinkKind = LinkCandidate['kind'];

export interface DocumentLinePickerValue {
  kind: LinkKind;
  lineId: number;
}

const KIND_ORDER: readonly LinkKind[] = ['deal', 'quote', 'invoice'];

const KIND_LABEL: Record<LinkKind, string> = {
  deal: 'Deal',
  quote: 'Quote',
  invoice: 'Invoice',
};

function sameLine(a: DocumentLinePickerValue | null, candidate: LinkCandidate): boolean {
  return a !== null && a.kind === candidate.kind && a.lineId === candidate.lineId;
}

export function DocumentLinePicker(props: {
  candidates: LinkCandidate[] | null;
  loading: boolean;
  error: string | null;
  value: { kind: 'deal' | 'quote' | 'invoice'; lineId: number } | null;
  onChange: (v: { kind: 'deal' | 'quote' | 'invoice'; lineId: number } | null) => void;
}) {
  const { candidates, loading, error, value, onChange } = props;
  const name = useId();

  if (error !== null) {
    return (
      <Notice tone="danger" role="alert">
        {error}
      </Notice>
    );
  }

  if (loading || candidates === null) {
    return (
      <div className="space-y-2" aria-busy="true">
        {[0, 1, 2].map((index) => (
          <div key={index} className="rounded-md border border-border px-3 py-2.5">
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="mt-2 h-3 w-3/4" />
          </div>
        ))}
      </div>
    );
  }

  if (candidates.length === 0) {
    return (
      <EmptyState
        className="py-6"
        title="No deal, quote or invoice line sells this product to this client"
        description="Raise the line on a deal, quote or invoice first, then link this row to it."
      />
    );
  }

  return (
    <div className="space-y-3">
      {KIND_ORDER.map((kind) => {
        const rows = candidates.filter((candidate) => candidate.kind === kind);
        if (rows.length === 0) return null;
        return (
          <fieldset key={kind} className="min-w-0">
            <legend className="mb-1 text-xs font-medium text-fg-muted">{KIND_LABEL[kind]}</legend>
            <div className="hidden grid-cols-[minmax(0,2fr)_minmax(0,1.4fr)_5rem_minmax(0,1.6fr)] gap-3 px-3 pb-1 pl-9 text-xs text-fg-subtle md:grid">
              <span>{KIND_LABEL[kind]}</span>
              <span>Project</span>
              <span className="text-right">Quantity</span>
              <span>Cities</span>
            </div>
            <ul className="space-y-1.5">
              {rows.map((candidate) => {
                const checked = sameLine(value, candidate);
                const inputId = `${name}-${candidate.kind}-${candidate.lineId}`;
                return (
                  <li key={`${candidate.kind}:${candidate.lineId}`}>
                    <label
                      htmlFor={inputId}
                      className={cx(
                        'flex cursor-pointer gap-2.5 rounded-md border px-3 py-2.5 max-sm:min-h-11',
                        checked
                          ? 'border-accent-brand bg-surface-sunken'
                          : 'border-border hover:bg-surface-sunken',
                      )}
                    >
                      <input
                        id={inputId}
                        type="radio"
                        name={name}
                        checked={checked}
                        onChange={() =>
                          onChange({ kind: candidate.kind, lineId: candidate.lineId })
                        }
                        className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-accent-brand"
                      />
                      <span className="grid min-w-0 flex-1 gap-1 text-sm md:grid-cols-[minmax(0,2fr)_minmax(0,1.4fr)_5rem_minmax(0,1.6fr)] md:items-start md:gap-3">
                        <span className="min-w-0">
                          <span className="flex flex-wrap items-center gap-1.5">
                            <span className="truncate font-medium text-fg">
                              {candidate.documentLabel}
                            </span>
                            {candidate.documentStatus !== '' && (
                              <Badge tone="neutral">{candidate.documentStatus}</Badge>
                            )}
                          </span>
                          {(candidate.recommended || candidate.onTracker) && (
                            <span className="mt-1 flex flex-wrap gap-1.5">
                              {candidate.recommended && <Badge tone="accent">Recommended</Badge>}
                              {candidate.onTracker && (
                                <Badge tone="neutral" variant="outline">
                                  Already on the tracker
                                </Badge>
                              )}
                            </span>
                          )}
                        </span>
                        <span className="min-w-0 text-fg-secondary">
                          <span className="text-fg-subtle md:hidden">Project </span>
                          {candidate.project?.name ?? '—'}
                        </span>
                        <span className="text-fg-secondary tabular-nums md:text-right">
                          <span className="text-fg-subtle md:hidden">Quantity </span>
                          {candidate.quantity}
                        </span>
                        <span className="min-w-0 text-fg-secondary">
                          <span className="text-fg-subtle md:hidden">Cities </span>
                          {candidate.cities.length > 0 ? candidate.cities.join(', ') : '—'}
                        </span>
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </fieldset>
        );
      })}
    </div>
  );
}
